import { ALLOWED_EMAIL_DOMAIN, canonicalizeEmail, isAllowedEmailDomain } from './identity';
import { ROLES, type Role } from './enums';
import { parseCsv } from './kra-import';

/**
 * Bulk onboarding.
 *
 * The columns are the Onboard employee dialog's fields, in its order, because the
 * sheet is that form filled in a hundred times - anything the form asks for and the
 * sheet does not would be a field HR could only set by opening every row afterwards.
 *
 * Department, designation and reporting manager are NAMES here, not ids: nobody types
 * a cuid into a spreadsheet. They are matched to the org by the importer, and an
 * unmatched one blocks that row by name rather than being invented - a department
 * created from a typo is one nobody notices until the org chart has two of them.
 *
 * The email rules are the sign-in rules, applied here so a bad address fails on its
 * own line instead of as a rejected POST 40 rows in. Canonicalised before the domain
 * check and stored canonical, the same order the auth path uses: an address stored raw
 * here would never match the sign-in lookup, and a SECOND record would be
 * auto-provisioned beside it. → canonicalizeEmail
 */
export interface ImportedEmployee {
  /** The full name. Also what the preview reads. */
  name: string;
  /** Canonical - trimmed, lowercased, `+tag` stripped, dots left alone. */
  email: string;
  employeeCode: string | null;
  role: Role;
  /** Matched to the org by name; null when the column was left blank. */
  departmentName: string | null;
  designationTitle: string | null;
  reportingManagerEmail: string | null;
  /** PlainDate, `YYYY-MM-DD`. */
  joinedAt: string | null;
}

export interface PeopleImportResult {
  employees: ImportedEmployee[];
  errors: string[];
}

export const PEOPLE_IMPORT_HEADERS = [
  'Work Email',
  'Full Name',
  'Employee ID',
  'Department',
  'Designation',
  'Reporting Manager Email',
  'Role',
  'Joining Date',
] as const;

/** Mirrors the zod limits in people/dto.ts, so a long cell fails by line. */
const MAX_NAME = 120;
const MAX_CODE = 32;

/** Accepts the label HR reads in the dropdown as well as the stored value. */
const ROLE_ALIASES: Record<string, Role> = {
  employee: 'EMPLOYEE',
  manager: 'MANAGER',
  'reporting manager': 'MANAGER',
  dept_head: 'DEPT_HEAD',
  'dept head': 'DEPT_HEAD',
  'department head': 'DEPT_HEAD',
  hr: 'HR',
  super_admin: 'SUPER_ADMIN',
  'super admin': 'SUPER_ADMIN',
  management: 'SUPER_ADMIN',
};

export function parsePeopleCsv(text: string): PeopleImportResult {
  const rows = parseCsv(text);
  const errors: string[] = [];
  const employees: ImportedEmployee[] = [];
  const seen = new Set<string>();

  const hasHeader = rows[0]?.[0]?.trim().toLowerCase() === 'work email';
  const body = hasHeader ? rows.slice(1) : rows;
  const firstLine = hasHeader ? 2 : 1;

  body.forEach((cells, index) => {
    const line = firstLine + index;
    const [
      rawEmail = '',
      name = '',
      code = '',
      department = '',
      designation = '',
      managerEmail = '',
      rawRole = '',
      joinedAt = '',
    ] = cells.map((c) => c.trim());

    if (cells.every((c) => c.trim() === '')) return;

    if (rawEmail === '') {
      errors.push(`Line ${line}: Work Email is required.`);
      return;
    }
    const email = canonicalizeEmail(rawEmail);
    if (!isAllowedEmailDomain(email)) {
      errors.push(`Line ${line}: ${rawEmail} is not on the @${ALLOWED_EMAIL_DOMAIN} domain.`);
      return;
    }
    // Two rows for one person is a paste slip, not two hires. Caught here rather than
    // left to the second POST, which would report "already on the team" about somebody
    // this very import had just created.
    if (seen.has(email)) {
      errors.push(`Line ${line}: ${email} appears twice in this file.`);
      return;
    }

    if (name === '') {
      errors.push(`Line ${line}: Full Name is required.`);
      return;
    }
    if (name.length > MAX_NAME) {
      errors.push(`Line ${line}: Full Name is at most ${MAX_NAME} characters.`);
      return;
    }
    if (code.length > MAX_CODE) {
      errors.push(`Line ${line}: Employee ID is at most ${MAX_CODE} characters.`);
      return;
    }

    const role = rawRole === '' ? 'EMPLOYEE' : ROLE_ALIASES[rawRole.toLowerCase()];
    if (!role || !ROLES.includes(role)) {
      errors.push(`Line ${line}: "${rawRole}" is not a role. Use ${ROLES.join(', ')}.`);
      return;
    }

    /**
     * `YYYY-MM-DD` only, and nothing else is guessed at.
     *
     * 03-04-2026 is 3 April to the person who typed it and 4 March to a parser that
     * assumes American order, and neither the sheet nor the record would ever show
     * which one it took. A refused line HR can fix beats a joining date that is
     * silently three weeks wrong.
     */
    let joined: string | null = null;
    if (joinedAt !== '') {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(joinedAt) || Number.isNaN(Date.parse(joinedAt))) {
        errors.push(`Line ${line}: Joining Date must be YYYY-MM-DD, e.g. 2026-04-01.`);
        return;
      }
      joined = joinedAt;
    }

    if (managerEmail !== '' && !isAllowedEmailDomain(canonicalizeEmail(managerEmail))) {
      errors.push(
        `Line ${line}: Reporting Manager Email must be on the @${ALLOWED_EMAIL_DOMAIN} domain.`,
      );
      return;
    }

    seen.add(email);
    employees.push({
      name,
      email,
      // Uppercased like the DTO does, so inn0020 and INN0020 cannot become two people.
      employeeCode: code === '' ? null : code.toUpperCase(),
      role,
      departmentName: department === '' ? null : department,
      designationTitle: designation === '' ? null : designation,
      reportingManagerEmail: managerEmail === '' ? null : canonicalizeEmail(managerEmail),
      joinedAt: joined,
    });
  });

  return { employees, errors };
}
